import { useState, useEffect, useMemo } from 'react';
import api, { downloadCsv, exportIdsParam } from '../utils/api';
import ClearFiltersButton from '../components/common/ClearFiltersButton';
import { useAuth } from '../context/AuthContext';
import { useUrlParams } from '../hooks/useUrlState';
import Pagination from '../components/Pagination';
import Badge from '../components/Badge';
import ActionsDropdown from '../components/ActionsDropdown';
import StateBlock from '../components/StateBlock';
import CsvImportModal from '../components/CsvImportModal';
import VendorPanel from '../components/VendorPanel';
import VendorEditDrawer from '../components/VendorEditDrawer';
import PayeeReviewPanel from '../components/PayeeReviewPanel';
import SortTh, { useSort } from '../components/common/SortableHeader';
import AlphaFilter from '../components/common/AlphaFilter';

export default function VendorsPage() {
  const { user } = useAuth();
  const [vendors, setVendors] = useState(null);
  const [importingPayees, setImportingPayees] = useState(false);
  const [payeeImportResult, setPayeeImportResult] = useState(null);
  const [reviewItems, setReviewItems] = useState(null);
  const [error, setError] = useState(false);
  const { get, set } = useUrlParams();
  const search = get('search', '');
  const alphaFilter = get('alpha', '');
  const statusFilter = get('status', 'all');
  const page = get('page', 1);
  const pageSize = get('pageSize', 25);

  const [panelId, setPanelId] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [showImport, setShowImport] = useState(false);

  const { sortKey, sortDir, handleSort, sortData } = useSort('name', 'asc');

  function load() {
    setError(false);
    api.get('/vendors')
      .then(r => setVendors(r.data))
      .catch(() => setError(true));
  }

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!vendors) return [];
    return vendors.filter(v => {
      if (search && !v.name.toLowerCase().includes(search.toLowerCase()) &&
        !(v.contact_name || '').toLowerCase().includes(search.toLowerCase()) &&
        !(v.email || '').toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter === 'active' && !v.is_active) return false;
      if (statusFilter === 'inactive' && v.is_active) return false;
      if (alphaFilter && v.name.charAt(0).toUpperCase() !== alphaFilter) return false;
      return true;
    });
  }, [vendors, search, statusFilter, alphaFilter]);

  const sorted = useMemo(() => {
    return sortData(filtered, {
      name: (v) => v.name || '',
      contact_name: (v) => v.contact_name || '',
      email: (v) => v.email || '',
      active_contractor_count: (v) => v.active_contractor_count || 0,
      is_active: (v) => v.is_active ? 1 : 0,
    });
  }, [filtered, sortKey, sortDir]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  function handleFilterChange(key, value) {
    set({ [key]: value, page: 1 });
  }

  async function handleExport() {
    await downloadCsv('/vendors/export', 'vendors-export.csv', exportIdsParam(filtered, vendors));
  }

  async function handlePayeeImport(file) {
    setImportingPayees(true);
    setPayeeImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await api.post('/payee-import/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPayeeImportResult(r.data);
      if (r.data.needsReview && r.data.needsReview.length > 0) setReviewItems(r.data.needsReview);
      load();
    } catch (e) {
      setPayeeImportResult({ message: e.response?.data?.error || 'Import failed', warnings: [] });
    } finally {
      setImportingPayees(false);
    }
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            Vendor Management {vendors && <span className="page-title-count">({vendors.length} total)</span>}
          </h1>
          <p className="page-subtitle">Manage vendor profiles, bank info, and contractor placements.</p>
        </div>
        <div className="page-actions">
          {user?.role === 'admin' && (
            <button className="btn btn-secondary" onClick={handleExport}>↓ Export CSV</button>
          )}
          {user?.role === 'admin' && (
            <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
              {importingPayees ? 'Importing…' : '↑ Import Payee Bank Details'}
              <input type="file" accept=".csv" style={{ display: 'none' }} onChange={e => e.target.files[0] && handlePayeeImport(e.target.files[0])} />
            </label>
          )}
          <button className="btn btn-secondary" onClick={() => setShowImport(true)}>↑ Upload CSV</button>
          <button className="btn btn-primary" onClick={() => setEditTarget({})}>+ Add Vendor</button>
        </div>
      </div>
      {reviewItems && (
        <PayeeReviewPanel
          items={reviewItems}
          onClose={() => setReviewItems(null)}
          onDone={() => { setReviewItems(null); load(); }}
        />
      )}
      {payeeImportResult && (
        <div style={{ background: 'var(--info-bg)', color: 'var(--info-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 16 }}>
          {payeeImportResult.message}
          {payeeImportResult.warnings && payeeImportResult.warnings.length > 0 && (
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 18, maxHeight: 200, overflowY: 'auto' }}>
              {payeeImportResult.warnings.map((w, i) => <li key={i} style={{ marginBottom: 4 }}>{w}</li>)}
            </ul>
          )}
        </div>
      )}

      <div className="filter-bar">
        <input
          type="text"
          className="filter-search"
          placeholder="Search vendor, contact, or email…"
          value={search}
          onChange={e => handleFilterChange('search', e.target.value)}
        />
        <select value={statusFilter} onChange={e => handleFilterChange('status', e.target.value)}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <ClearFiltersButton
          active={!!(search || alphaFilter || statusFilter !== 'all')}
          onClear={() => set({ search: '', alpha: '', status: '', page: 1 })}
        />
      </div>

      <AlphaFilter items={vendors || []} value={alphaFilter} onChange={l => handleFilterChange('alpha', l)} />

      {error && <StateBlock type="error" action={<button className="btn btn-primary" onClick={load}>Try again</button>} />}
      {!error && vendors === null && <StateBlock type="loading" />}
      {!error && vendors !== null && filtered.length === 0 && (
        <StateBlock type="noResults" message="No vendors match this search. Clear filters or add a vendor." />
      )}

      {!error && filtered.length > 0 && (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <SortTh label="Vendor Name" sortKey="name" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortTh label="Contact" sortKey="contact_name" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortTh label="Email" sortKey="email" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortTh label="Active Contractors" sortKey="active_contractor_count" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortTh label="Status" sortKey="is_active" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map(v => (
                <tr key={v.id}>
                  <td>
                    <button className="row-name-link" onClick={() => setPanelId(v.id)}>{v.name}</button>
                  </td>
                  <td>{v.contact_name || '—'}</td>
                  <td>{v.email || '—'}</td>
                  <td>{v.active_contractor_count}</td>
                  <td><Badge variant={v.is_active ? 'success' : 'neutral'}>{v.is_active ? 'Active' : 'Inactive'}</Badge></td>
                  <td style={{ textAlign: 'right' }}>
                    <ActionsDropdown actions={[
                      { label: 'View', onClick: () => setPanelId(v.id) },
                      { label: 'Edit', onClick: () => setEditTarget(v) },
                    ]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={page} pageSize={pageSize} total={filtered.length}
            onPageChange={(p) => set({ page: p })}
            onPageSizeChange={(s) => set({ pageSize: s, page: 1 })}
          />
        </>
      )}

      {panelId && (
        <VendorPanel
          vendorId={panelId}
          onClose={() => setPanelId(null)}
          onEdit={(data) => { setPanelId(null); setEditTarget(data); }}
        />
      )}

      {editTarget !== null && (
        <VendorEditDrawer
          vendor={editTarget.id ? editTarget : null}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); }}
        />
      )}

      {showImport && (
        <CsvImportModal
          entityLabel="Vendor"
          importPath="/vendors/import"
          onClose={() => setShowImport(false)}
          onComplete={load}
        />
      )}
    </div>
  );
}
